import { FormProps } from '@/types/form/form-props';
import { CreateSaleInput, CreateSaleInputSchema } from '../api/create-sale';
import { useForm, useStore } from '@tanstack/react-form';
import { fieldToInputProps, toFormProps } from '@/utils/shared/form';
import { TextInput } from '@/shared/components/ui/inputs/text-input';
import { SelectInput, SelectOption } from '@/shared/components/ui/inputs/select-input';
import { SliderInput } from '@/shared/components/ui/inputs/slider-input';
import { DefaultFormFooter } from '@/shared/components/ui/form/default-form-footer';
import links from '@/config/links.config';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { BonusCodeInput } from '@/features/bonus-codes/components/bonus-code-input';
import { estimateProfit } from '@/utils/sale/profit';
import {
  BonusCodeSchema,
  checkBonusCodeQueryOptions,
  normalizeBonusCode,
} from '@/features/bonus-codes/api/check-bonus-code';
import { usePendingBonusCodeStore } from '@/features/bonus-codes/stores/pending-bonus-code.store';

type CreateSaleFormProps = FormProps<CreateSaleInput> & {
  roundOptions: SelectOption[];
};

export function CreateSaleForm({
  roundOptions,
  defaultValues,
  onSubmit,
  Footer = DefaultFormFooter,
  ...rest
}: CreateSaleFormProps) {
  const queryClient = useQueryClient();
  // From a shared "Tipsa en kompis" link the friend opened before signing up.
  const pendingCode = usePendingBonusCodeStore((s) => s.code);
  const clearPendingCode = usePendingBonusCodeStore((s) => s.setCode);

  const validateBonusCode = async ({ value }: { value?: string }) => {
    if (!value?.trim()) return undefined;

    const format = BonusCodeSchema.safeParse(value);
    if (!format.success) return format.error.issues[0].message;

    try {
      const { valid } = await queryClient.fetchQuery(checkBonusCodeQueryOptions(value));
      return valid ? undefined : 'Bonuskoden finns inte, har gått ut eller är förbrukad';
    } catch {
      return 'Kunde inte kontrollera bonuskoden. Försök igen.';
    }
  };

  const form = useForm({
    defaultValues: {
      name: `Försäljning ${roundOptions[0].label}`,
      roundId: roundOptions[0].value,
      participationItemCountTarget: 10,
      expectedParticipationCount: 30,
      ...(pendingCode && { bonusCode: pendingCode }),
      ...defaultValues,
    } as CreateSaleInput,
    validators: { onSubmit: CreateSaleInputSchema },
    onSubmit: async ({ value: { bonusCode, ...value } }) => {
      await onSubmit(bonusCode?.trim() ? { ...value, bonusCode: normalizeBonusCode(bonusCode) } : value);
      clearPendingCode(null);
    },
  });

  // A filled-in code isn't typed, so check it right away to show what it gives.
  useEffect(() => {
    if (form.getFieldValue('bonusCode')?.trim()) form.validateField('bonusCode', 'change');
  }, [form]);

  const participationItemCountTarget = useStore(form.store, (s) => s.values.participationItemCountTarget);
  const expectedParticipationCount = useStore(form.store, (s) => s.values.expectedParticipationCount);

  const bonusCode = useStore(form.store, (s) => s.values.bonusCode);
  // Filled by the bonus code validation; re-read when it finishes.
  useStore(form.store, (s) => s.fieldMeta.bonusCode?.isValidating);
  const check = bonusCode?.trim() ? queryClient.getQueryData(checkBonusCodeQueryOptions(bonusCode).queryKey) : null;
  const bonus = check?.valid ? check.bonus : null;
  const kind = check?.valid ? check.kind : null;

  const profit = estimateProfit(expectedParticipationCount, participationItemCountTarget, bonus);

  return (
    <form {...rest} {...toFormProps(form)}>
      <form.Field name="name">{(field) => <TextInput {...fieldToInputProps(field, 'Namn')} />}</form.Field>

      <form.Field name="roundId">
        {(field) => <SelectInput {...fieldToInputProps(field, 'Sista beställningsdag')} options={roundOptions} />}
      </form.Field>

      <form.Field name="expectedParticipationCount">
        {(field) => <SliderInput {...fieldToInputProps(field, 'Antal säljare (ca)')} step={1} min={5} max={50} />}
      </form.Field>

      <form.Field name="participationItemCountTarget">
        {(field) => (
          <SliderInput {...fieldToInputProps(field, 'Mål produkter / säljare')} step={1} min={1} max={40} />
        )}
      </form.Field>

      <form.Field
        name="bonusCode"
        validators={{ onChangeAsync: validateBonusCode, onChangeAsyncDebounceMs: 400 }}
      >
        {(field) => (
          <BonusCodeInput
            {...fieldToInputProps(field, 'Bonuskod')}
            isValidating={field.state.meta.isValidating}
            bonus={bonus}
            kind={kind}
          />
        )}
      </form.Field>

      <p className="md:col-span-2 text-sm text-muted-foreground">
        Produkterna som säljs sätts av försäljningsomgången. Se{' '}
        <a className="underline cursor-pointer" href={`${links.landing}/produkter`} target="_blank" rel="noreferrer">
          vilka produkter som går att sälja
        </a>
        .
      </p>

      <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting]}>
        {([canSubmit, isSubmitting]) => (
          <Footer
            canSubmit={canSubmit}
            isSubmitting={isSubmitting}
            submitText={`Tjäna ${profit.toLocaleString('sv-se')} kr!`}
          />
        )}
      </form.Subscribe>
    </form>
  );
}
